import { SCHEMATIC_EXAMPLE_ROUTES } from "@/lib/schematic-library-routes";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { WiringSchematic } from "@/components/resources/wiring-schematic";
import { WiringSchematicCapture } from "@/components/resources/wiring-schematic-capture";
import { resourceExampleFromUrl, resourceExampleSystem } from "@/lib/resource-example-handoff";
import { useCableSizeVisibility } from "@/lib/settings/cable-size-visibility";
import { useCableLengthVisibility } from "@/lib/settings/cable-length-visibility";
import { useCableVdropVisibility } from "@/lib/settings/cable-vdrop-visibility";
import { useFuseVisibility } from "@/lib/settings/fuse-visibility";
import { useProductImageVisibility } from "@/lib/settings/product-image-visibility";
import { useI18n } from "@/lib/i18n";
import { useUnits } from "@/lib/units";
import { TOOL_EXAMPLES, type ToolExampleId } from "@/lib/tool-example-systems";
import "./index.css";

// Each frame owns the real canvas and its stores. Two examples on one page
// must never replace each other's drawing, hover state, or an editor project.
// AppShell/persistence/telemetry are deliberately absent from this entry.
const url = new URL(window.location.href);
const handoff = resourceExampleFromUrl(url);
const library = SCHEMATIC_EXAMPLE_ROUTES.some(route => route.id === handoff?.example);
const capture = library && url.searchParams.getAll("preview").length === 1 && url.searchParams.get("preview") === "1";
const system = handoff ? resourceExampleSystem(handoff) : null;
const tool = handoff?.example === "tool-example" && Object.hasOwn(TOOL_EXAMPLES, handoff.variant)
  ? handoff.variant as ToolExampleId : undefined;
if (system && handoff) {
  // In-memory overrides: viewing an example must not save editor preferences.
  useUnits.setState({ units: handoff.units ?? "metric" });
  useI18n.setState({ locale: "en" });
  useFuseVisibility.setState({ visible: false });
  useCableSizeVisibility.setState({ visible: false });
  useCableLengthVisibility.setState({ visible: false });
  useCableVdropVisibility.setState({ visible: false });
  useProductImageVisibility.setState({ visible: true });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {system ? <div className="h-dvh w-full" data-wiring-canvas>
      {capture ? <WiringSchematicCapture system={system} /> :
        <WiringSchematic system={system} embedded={window.self !== window.top} tool={tool} library={library}
          initialMode={library || handoff?.example === "single-battery-boat" ? "edit" : "simulate"} />}
    </div> : <p className="p-6">This wiring example could not be found.</p>}
  </StrictMode>,
);
